import type { RewardType } from '@arrows/shared';
import type { GameOverDetail } from '../game/events';
import type { AdPlacementId, AdShowResult } from '../platform/ads/types';
import type { ConsentDialogApi } from './consentDialog';
import type { ContinueDialogApi } from './continueDialog';
import type { ActiveModal, ModalsApi } from './modals';
import type { LevelFlowPort } from './ports';

type AdResultHandler = (result: AdShowResult) => void | Promise<void>;

interface AdHandlers {
  readonly onCompleted?: AdResultHandler;
  readonly onNotCompleted?: AdResultHandler;
}

interface ConsentActions {
  readonly onAccept: () => void | Promise<void>;
  readonly onCancel: () => void;
}

export interface AdFlowDeps {
  readonly adsService: { show(placementId: AdPlacementId): Promise<AdShowResult> };
  readonly levelFlow: LevelFlowPort;
  readonly modals: Pick<ModalsApi, 'active' | 'setActive'>;
  readonly continueDialog: Pick<ContinueDialogApi, 'show' | 'clear' | 'setError'>;
  readonly consentDialog: Pick<ConsentDialogApi, 'show'>;
  readonly emitContinue: (detail: { levelNumber: number }) => void;
  readonly emitHintGranted: (detail: { levelNumber: number }) => void;
  readonly recordReward: (rewardType: RewardType) => void;
  readonly setHeaderWarning: (message: string) => void;
  readonly disableResultActions: () => void;
  readonly sync: () => void;
}

export interface AdFlowApi {
  readonly active: boolean;
  resetTransientState(): void;
  handleGameOver(detail: GameOverDetail): void;
  requestHint(): void;
  requestContinue(): void;
  requestRestart(): void;
  requestNextLevel(): Promise<void>;
  acceptConsent(): Promise<void>;
  cancelConsent(): void;
  destroy(): void;
}

export function createAdFlow(deps: AdFlowDeps): AdFlowApi {
  let currentGameOver: GameOverDetail | null = null;
  let consentActions: ConsentActions | null = null;
  let active = false;

  const requestRewardConsent = (rewardText: string, actions: ConsentActions): void => {
    deps.consentDialog.show(rewardText);
    consentActions = actions;
    deps.modals.setActive('consent');
  };

  const runAd = async (
    placementId: AdPlacementId,
    capturedEpoch: number,
    handlers: AdHandlers = {},
  ): Promise<void> => {
    if (active) {
      return;
    }
    active = true;
    try {
      deps.modals.setActive('ad');
      const result = await deps.adsService.show(placementId);
      if (capturedEpoch !== deps.levelFlow.epoch) {
        // stale な結果は適用しないが、モーダル状態は復旧する。閉じるのは
        // この広告対話に属する 'ad' と（広告中の再入で開いた）'consent' だけで、
        // 新しい epoch 側のフローが設定したモーダルは踏み潰さない。
        const modal = deps.modals.active;
        if (modal === 'ad' || modal === 'consent') {
          consentActions = null;
          deps.modals.setActive('none');
        }
        return;
      }
      deps.modals.setActive('none');
      const handler = result === 'completed' ? handlers.onCompleted : handlers.onNotCompleted;
      await handler?.(result);
    } finally {
      active = false;
      deps.sync();
    }
  };

  const requestHint = (): void => {
    const capturedEpoch = deps.levelFlow.epoch;
    const levelNumber = deps.levelFlow.currentLevel;
    requestRewardConsent(
      '外に出せる矢印を 1 本、光らせて教えます。',
      {
        onAccept: () => runAd('hint-reveal', capturedEpoch, {
          onCompleted: () => {
            deps.recordReward('hint');
            deps.emitHintGranted({ levelNumber });
          },
          onNotCompleted: () => deps.setHeaderWarning('広告を取得できませんでした'),
        }),
        onCancel: () => deps.modals.setActive('none'),
      },
    );
  };

  const requestContinue = (): void => {
    const detail = currentGameOver;
    if (detail === null) {
      return;
    }
    deps.continueDialog.clear();
    const capturedEpoch = deps.levelFlow.epoch;
    requestRewardConsent(
      '失敗の回数をリセットして、この盤面を続けられます。',
      {
        onAccept: () => runAd('continue', capturedEpoch, {
          onCompleted: () => {
            currentGameOver = null;
            deps.recordReward('continue');
            deps.emitContinue({ levelNumber: detail.levelNumber });
          },
          onNotCompleted: () => {
            deps.continueDialog.setError('広告を取得できませんでした');
            deps.modals.setActive('continue');
          },
        }),
        onCancel: () => deps.modals.setActive('continue'),
      },
    );
  };

  const requestRestart = (): void => {
    if (currentGameOver === null) {
      return;
    }
    deps.continueDialog.clear();
    const capturedEpoch = deps.levelFlow.epoch;
    const restartAfterAd = (): void => {
      currentGameOver = null;
      deps.levelFlow.restartAfterAd();
    };
    requestRewardConsent(
      '広告の後、このレベルを最初からやり直します。',
      {
        onAccept: () => runAd('restart', capturedEpoch, {
          onCompleted: restartAfterAd,
          onNotCompleted: restartAfterAd,
        }),
        onCancel: () => deps.modals.setActive('continue'),
      },
    );
  };

  const requestNextLevel = async (): Promise<void> => {
    deps.disableResultActions();
    const capturedEpoch = deps.levelFlow.epoch;
    await runAd('next-level', capturedEpoch, {
      onCompleted: deps.levelFlow.loadNextLevel,
      onNotCompleted: deps.levelFlow.loadNextLevel,
    });
  };

  const acceptConsent = async (): Promise<void> => {
    const onAccept = consentActions?.onAccept;
    consentActions = null;
    await onAccept?.();
  };

  const cancelConsent = (): void => {
    const onCancel = consentActions?.onCancel;
    consentActions = null;
    onCancel?.();
  };

  return {
    get active() {
      return active;
    },
    resetTransientState: () => {
      currentGameOver = null;
      consentActions = null;
      const modal: ActiveModal = deps.modals.active;
      if (modal === 'continue' || modal === 'consent') {
        deps.modals.setActive('none');
      }
    },
    handleGameOver: (detail) => {
      if (detail.levelNumber !== deps.levelFlow.currentLevel) {
        return;
      }
      currentGameOver = detail;
      deps.continueDialog.show(detail);
      deps.modals.setActive('continue');
    },
    requestHint,
    requestContinue,
    requestRestart,
    requestNextLevel,
    acceptConsent,
    cancelConsent,
    destroy: () => {},
  };
}
